import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector } from '@core/js/store/hooks';
import { get } from '@core/js/utils/fetch.util';

import { useDataFetchWithAwait } from '@interlayer/js/app/hooks/useDataFetchWithAwait';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { usePostCodeSelector } from '@interlayer/js/app/store/hooks/usePostCodeSelector';
import { setAddresses } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import {
    TAddressAdditionalParams,
    TAddressData,
    TAddressLocationResponse } from '@interlayer/js/app/store/reducers/postCodeFinder/types';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { parseQueryString } from '@interlayer/js/app/utils/parseQueryString.util';

import { highlightMatches } from '../utils/highlightMatches';

export const useFindAddress = () => {
    const dispatch = useDispatch();
    const defaultCountry = useAppSelector(prop('defaultCountry'));
    const { addresses } = usePostCodeSelector();
    const t = useTranslations('playtech.mobile.system.registration.');
    const [
        dTagPostcodeFindConfigurationUrl,
        dTagKeyPostCode
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.postcodeFindConfigurationUrl,
        DYNAMIC_TAG_NAMES.postcodeConfigurationKey
    ]);

    const checkIsData = useCallback(({ additionalParams: address }: TAddressAdditionalParams): false | TAddressData => {
        if (address && address in addresses) {
            return addresses[address];
        }

        return false;
    }, [addresses]);

    const onResponse = useCallback((response: TAddressLocationResponse, additionalParams?: string): TAddressData => {
        const items = response.Items || [];

        if (items[0]?.Error) {
            throw new Error();
        }

        const addressesData: TAddressData = {
            data: items,
            options: items.map(({ Id, Text, Description, Type, Highlight }) => {
                const [highlightText = '', highlightDescription = ''] = Highlight.split(';');
                const label = `${highlightMatches({ str: Text, highlightIndexes: highlightText })} ${highlightMatches({ str: Description, highlightIndexes: highlightDescription })}`;

                return {
                    id: Id,
                    label,
                    originalText: `${Text} ${Description}`,
                    isNeedRequest: Type !== 'Address',
                    requestText: Text
                };
            })
        };

        if (additionalParams) {
            dispatch(setAddresses({ [additionalParams]: addressesData }));
        }

        return addressesData;
    }, [dispatch]);

    const onErrorPlace = useCallback(() => t('validation.zip-notFound'), [t]);

    const { fetch, ...other } = useDataFetchWithAwait({
        fetch: get,
        checkIsData,
        onResponse,
        onError: onErrorPlace
    });

    const fetchFindAddress = useCallback((address?: string, containerId?: string) => {
        if (!address) {
            return;
        }

        const addressKey = containerId ? `${address}_${containerId}` : address;

        return fetch({
            additionalParams: addressKey,
            paramsFetch: `${dTagPostcodeFindConfigurationUrl}?${parseQueryString({ key: dTagKeyPostCode, Text: address, Countries: defaultCountry, ...containerId && { Container: containerId } })}`
        });
    }, [dTagKeyPostCode, dTagPostcodeFindConfigurationUrl, defaultCountry, fetch]);

    return { ...other, fetchFindAddress };
};
